import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Alert, AutoComplete, Button, Descriptions, Form, Input, Skeleton, Space, Tooltip } from 'antd'
import { PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useToast } from '@/contexts/ToastContext'
import { permissions } from '@/types/authorization'
import type { McpFirmProfileState } from '@/types/mcp'
import { isForbidden, useMcpFirmProfile, useSaveMcpFirmProfileMutation } from '../hooks'
import { FIRM_PROFILE_LIMITS as LIMITS, maxChars, toFormValues, toProfile, type FirmProfileFormValues } from './form'
import { LineList, Part, RowControls } from './FormLists'

const READ_ONLY_MESSAGE = 'You don’t have permission to change the firm profile.'

function SubHeading({ children }: { children: string }) {
  return <div className="mb-2 font-medium text-gray-900">{children}</div>
}

type FirmProfilePanelProps = { clientId: string; clientName: string }

export default function FirmProfilePanel({ clientId, clientName }: FirmProfilePanelProps) {
  const firmProfile = useMcpFirmProfile(clientId)
  const [formVersion, setFormVersion] = useState(0)

  if (firmProfile.isLoading) return <Skeleton active paragraph={{ rows: 10 }} />
  if (!firmProfile.data) {
    if (!firmProfile.isError) return null
    return (
      <Alert
        type={isForbidden(firmProfile.error) ? 'info' : 'error'}
        showIcon
        message={firmProfile.error instanceof Error ? firmProfile.error.message : 'Couldn’t load the firm profile.'}
        action={
          isForbidden(firmProfile.error) ? undefined : (
            <Button onClick={() => void firmProfile.refetch()}>Try again</Button>
          )
        }
      />
    )
  }
  // Kept mounted through refetches, so a failed or newer one doesn't throw away what's being typed.
  return (
    <FirmProfileForm
      key={formVersion}
      clientId={clientId}
      clientName={clientName}
      state={firmProfile.data}
      refreshFailed={firmProfile.isError}
      onReload={() => setFormVersion((version) => version + 1)}
    />
  )
}

type FirmProfileFormProps = FirmProfilePanelProps & {
  state: McpFirmProfileState
  refreshFailed: boolean
  onReload: () => void
}

function FirmProfileForm({ clientId, clientName, state, refreshFailed, onReload }: FirmProfileFormProps) {
  const { addToast } = useToast()
  const [form] = Form.useForm<FirmProfileFormValues>()
  const save = useSaveMcpFirmProfileMutation(clientId)
  const canSave = useAuthorization().hasPermission(permissions.updateAgentBuilder)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [initial] = useState(state)
  const [editingFrom, setEditingFrom] = useState(state.updatedAt)
  // save.data covers the render between the save settling and editingFrom catching up.
  const changedElsewhere = !save.isPending && state.updatedAt !== editingFrom && state.updatedAt !== save.data?.updatedAt
  const prefill = initial.prefillFromXpm

  const peopleOptions = useMemo(
    () =>
      Array.from(new Set([...prefill.partners, ...prefill.clientManagers.map((manager) => manager.name)])).map(
        (name) => ({ value: name })
      ),
    [prefill]
  )

  const handleSave = async () => {
    let values: FirmProfileFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaveError(null)
    try {
      const saved = await save.mutateAsync(toProfile(values))
      setEditingFrom(saved.updatedAt)
      addToast('Saved. Your AI assistant uses this from the next question.', 'success')
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Couldn’t save the firm profile.')
    }
  }

  return (
    <Space orientation="vertical" size={16} className="w-full">
      {state.status === 'not_set_up' ? (
        <Alert
          type="info"
          showIcon
          message="Not set up yet. The quickest way is in your AI assistant: type ‘set up my firm’. Or fill it in here."
        />
      ) : (
        state.updatedBy && (
          <p className="m-0 text-sm text-gray-500">
            Changed by {state.updatedBy}
            {state.updatedAt ? `, ${dayjs(state.updatedAt).format('DD/MM/YYYY h:mm A')}` : ''}
          </p>
        )
      )}
      <p className="m-0 text-sm text-gray-600">
        What {clientName} told their AI assistant in “Set up my firm”. It’s read in every conversation.
      </p>
      {changedElsewhere && (
        <Alert
          type="warning"
          showIcon
          message={`${state.updatedBy || 'Someone'} changed the firm profile since you opened it.`}
          description={canSave ? 'Saving now replaces their changes.' : undefined}
          action={
            <Button size="small" onClick={onReload}>
              Load their version
            </Button>
          }
        />
      )}
      {refreshFailed && <Alert type="warning" showIcon message="Couldn’t check for newer changes to the firm profile." />}
      {saveError && <Alert type="error" showIcon message={saveError} />}
      {!canSave && <Alert type="info" showIcon message={READ_ONLY_MESSAGE} />}

      <Form
        form={form}
        layout="vertical"
        disabled={!canSave}
        initialValues={toFormValues(initial.profile, prefill)}
        className="space-y-4"
      >
        <Part title="Basics">
          <div className="grid gap-x-6 md:grid-cols-2">
            <Form.Item name="firmName" label="Firm name" rules={[maxChars(LIMITS.firmName)]}>
              <Input maxLength={LIMITS.firmName} />
            </Form.Item>
            <Form.Item
              name="financialYear"
              label="Financial year"
              rules={[maxChars(LIMITS.financialYear)]}
              extra={prefill.financialYearEnd ? `Your Power BI model’s financial year ends ${prefill.financialYearEnd}` : undefined}
            >
              <Input maxLength={LIMITS.financialYear} placeholder="e.g. 1 July to 30 June" />
            </Form.Item>
            <Form.Item
              name="size"
              label="Size"
              rules={[maxChars(LIMITS.size)]}
              extra={prefill.staff !== null ? `XPM shows ${prefill.staff} staff` : undefined}
            >
              <Input maxLength={LIMITS.size} placeholder="e.g. 4 partners and 23 staff" />
            </Form.Item>
          </div>
          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              { key: 'partners', label: 'Partners', children: prefill.partners.join(', ') || '-' },
              {
                key: 'managers',
                label: 'Client managers',
                children:
                  prefill.clientManagers.map((manager) => `${manager.name} (${manager.clients} clients)`).join(', ') ||
                  '-',
              },
            ]}
          />
          <p className="mt-2 mb-0 text-xs text-gray-500">From XPM. If something’s wrong here, fix it in XPM.</p>
        </Part>

        <Part title="Team structure and key people">
          <Form.Item name="practiceManager" label="Practice manager" className="max-w-md" rules={[maxChars(LIMITS.practiceManager)]}>
            <AutoComplete options={peopleOptions} filterOption maxLength={LIMITS.practiceManager} placeholder="Who runs the practice day to day" />
          </Form.Item>
          <SubHeading>Teams</SubHeading>
          <Form.List name="teams">
            {(fields, { add, remove, move }) => (
              <div className="mb-6">
                {fields.map((field, index) => (
                  <div key={field.key} className="flex items-start gap-2">
                    <Form.Item
                      name={[field.name, 'name']}
                      className="w-60"
                      rules={[{ required: true, whitespace: true, message: 'Name the team.' }, maxChars(LIMITS.teamName)]}
                    >
                      <Input maxLength={LIMITS.teamName} placeholder="Team, e.g. Tax" aria-label="Team name" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'lead']} className="grow" rules={[maxChars(LIMITS.teamLead)]}>
                      <AutoComplete options={peopleOptions} filterOption maxLength={LIMITS.teamLead} placeholder="Who leads it" aria-label="Team lead" />
                    </Form.Item>
                    <RowControls index={index} count={fields.length} label="team" move={move} remove={remove} />
                  </div>
                ))}
                {fields.length < LIMITS.teams && (
                  <Button icon={<PlusOutlined />} onClick={() => add({ name: '', lead: '' })}>
                    Add a team
                  </Button>
                )}
              </div>
            )}
          </Form.List>
          <SubHeading>Key people</SubHeading>
          <Form.List name="keyPeople">
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className="flex items-start gap-2">
                    <Form.Item
                      name={[field.name, 'name']}
                      className="w-60"
                      rules={[{ required: true, whitespace: true, message: 'Give their name.' }, maxChars(LIMITS.keyPersonName)]}
                    >
                      <AutoComplete options={peopleOptions} filterOption maxLength={LIMITS.keyPersonName} placeholder="Name" aria-label="Key person" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'role']} className="grow" rules={[maxChars(LIMITS.keyPersonRole)]}>
                      <Input maxLength={LIMITS.keyPersonRole} placeholder="What they do, e.g. Does debtors" aria-label="Their role" />
                    </Form.Item>
                    <RowControls index={index} count={fields.length} label="key person" move={move} remove={remove} />
                  </div>
                ))}
                {fields.length < LIMITS.keyPeople && (
                  <Button icon={<PlusOutlined />} onClick={() => add({ name: '', role: '' })}>
                    Add a key person
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Part>

        <Part title="Job naming structure">
          <Form.Item
            name="jobNaming"
            className="!mb-0"
            rules={[maxChars(LIMITS.jobNaming)]}
            extra="How you name jobs, e.g. client code - service - year"
          >
            <Input.TextArea maxLength={LIMITS.jobNaming} showCount autoSize={{ minRows: 2, maxRows: 6 }} aria-label="Job naming structure" />
          </Form.Item>
        </Part>

        <Part title="Workflow notes">
          <p className="mt-0 mb-3 text-xs text-gray-500">
            e.g. We invoice at every month end. Job partners review all jobs before they go out. Admin does debtors.
          </p>
          <LineList
            name="workflowNotes"
            label="workflow note"
            max={LIMITS.lines}
            maxLength={LIMITS.note}
            addLabel="Add a workflow note"
            emptyMessage="Write the note, or remove it."
          />
        </Part>

        <Part title="Reporting notes">
          <p className="mt-0 mb-3 text-xs text-gray-500">
            e.g. We have a management meeting every month for business performance, and a team meeting weekly for workflow.
          </p>
          <LineList
            name="reportingNotes"
            label="reporting note"
            max={LIMITS.lines}
            maxLength={LIMITS.note}
            addLabel="Add a reporting note"
            emptyMessage="Write the note, or remove it."
          />
        </Part>

        <Part title="Objectives and key initiatives">
          <Form.List name="objectives">
            {(fields, { add, remove, move }) => (
              <Space orientation="vertical" size={12} className="w-full">
                {fields.map((field, index) => (
                  <div key={field.key} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start gap-2">
                      <Form.Item
                        name={[field.name, 'objective']}
                        className="grow"
                        rules={[{ required: true, whitespace: true, message: 'Write the objective, or remove it.' }, maxChars(LIMITS.objective)]}
                      >
                        <Input maxLength={LIMITS.objective} placeholder="e.g. Increase productivity by 10%" aria-label={`Objective ${index + 1}`} />
                      </Form.Item>
                      <RowControls index={index} count={fields.length} label="objective" move={move} remove={remove} />
                    </div>
                    <div className="pl-4">
                      <div className="mb-2 text-xs font-medium text-gray-600">Key initiatives</div>
                      <LineList
                        name={[field.name, 'initiatives']}
                        label="initiative"
                        max={LIMITS.initiatives}
                        maxLength={LIMITS.initiative}
                        addLabel="Add an initiative"
                        placeholder="e.g. Monitor timesheets every week"
                        emptyMessage="Write the initiative, or remove it."
                      />
                    </div>
                  </div>
                ))}
                {fields.length < LIMITS.objectives && (
                  <Button icon={<PlusOutlined />} onClick={() => add({ objective: '', initiatives: [] })}>
                    Add an objective
                  </Button>
                )}
              </Space>
            )}
          </Form.List>
        </Part>

        <Part title="Other notes">
          <p className="mt-0 mb-3 text-xs text-gray-500">e.g. We call debtors receivables.</p>
          <LineList
            name="notes"
            label="note"
            max={LIMITS.lines}
            maxLength={LIMITS.note}
            addLabel="Add a note"
            emptyMessage="Write the note, or remove it."
          />
        </Part>
      </Form>

      <div className="flex justify-end">
        <Tooltip title={canSave ? undefined : READ_ONLY_MESSAGE}>
          <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} disabled={!canSave} onClick={() => void handleSave()}>
            Save
          </Button>
        </Tooltip>
      </div>
    </Space>
  )
}
